import { useCallback, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { CampoDeImporte } from '../../../nucleo/importes/CampoDeImporte.jsx';
import { ENTRADA, interpretarImporte } from '../../../nucleo/importes/formatoDeImporte.js';
import {
  ListadoActualizando, ListadoCargando, ListadoConError,
} from '../../../nucleo/listados/EstadosDeListado.jsx';
import { ESTADO_DE_LISTADO, useConsultaDeListado } from '../../../nucleo/listados/useConsultaDeListado.js';
import {
  BASES, consultarSugerencias, eliminarLinea, guardarLineasDelMes,
  ORIGENES, propagarAMesesSiguientes,
} from '../api/presupuestoApi.js';
import { DialogoDePropagacion } from '../componentes/DialogoDePropagacion.jsx';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';
import {
  anioDePeriodo, DIVISA_DEL_PRESUPUESTO, etiquetaDePeriodo, importe, nombreDePeriodo,
  periodoDeFechaLocal, porcentaje,
} from '../formato.js';

/**
 * REGISTRO DEL PRESUPUESTO DE UN MES (RF-097, RF-098, RF-099, RF-106).
 *
 * ESTA PANTALLA NO CAPTURA EL PORCENTAJE Y NO PREVISUALIZA CON ÉL (D-152,
 * RN-231a): muestra el vigente como DATO DE LECTURA y lleva al asistente del año,
 * que es su único lugar de captura. Nada lo pide al abrir (RN-223c).
 *
 * OTRAS REGLAS QUE SE CONSTRUYEN AQUÍ:
 *   · La lista llega YA UNIDA y YA ORDENADA por el servidor —activas de gasto más
 *     las desactivadas con línea— y la pantalla no la reordena (D-138, D-139,
 *     RN-225, RN-226).
 *   · Un campo sin sugerencia se queda VACÍO, NUNCA EN CERO, con su explicación
 *     (RN-205, RN-222). Cero no es una forma de borrar: para eso está «Quitar
 *     presupuesto» (RN-192, RF-106).
 *   · Una cifra tecleada viaja como CAPTURA_MANUAL, aunque se haya copiado de la
 *     sugerencia, y la pantalla NUNCA reenvía el origen que leyó: mandar
 *     PROPAGADO_DE_OTRO_MES lo rechaza el servidor (RN-227, RN-234).
 *   · Tras guardar, UNA SOLA pregunta de propagación, y solo si el servidor la
 *     dispara con `categoriasConImporteCambiado` y `mesesSiguientesDelAnio`
 *     (RN-230b).
 *   · Sin candados por persona: ambas personas editan cualquier línea (RN-211).
 *
 * UNA SOLA OPERACIÓN ABASTECE ESTA PANTALLA (D-168, RN-235): §2 devuelve, junto
 * con `importeActual`, la IDENTIDAD de la línea ya guardada —`idPresupuestoDeCategoria`,
 * `version` (xmin) y `claveOrigenDeImporte`—. LOS CUATRO SE LEEN COMO UN BLOQUE:
 * nulos = ALTA, presentes = EDICIÓN, sin caso intermedio. Por eso esta pantalla
 * NO INVOCA §1 —el tablero— NI UNA VEZ: cruzar las dos listas para conseguir el
 * identificador y el testigo es exactamente el cálculo que D-139 y RNF-053
 * prohíben al cliente.
 *
 * `claveOrigenDeImporte` es PARA PINTAR LA ETIQUETA, NUNCA PARA REENVIARLO: lo
 * tecleado viaja siempre como CAPTURA_MANUAL (RN-227, RN-234).
 */
export default function RegistroDePresupuesto() {
  const { idPeriodo: idPeriodoDeLaRuta } = useParams();
  const [parametros] = useSearchParams();
  const hoy = obtenerFechaDeNegocioHoy();
  const idPeriodo = Number(idPeriodoDeLaRuta) || periodoDeFechaLocal(hoy);
  const idCategoriaEnFoco = Number(parametros.get('foco')) || null;

  // Lo tecleado, por categoría. Ausente = "no lo he tocado"; vacío = "lo dejé
  // vacío", que NO borra nada al guardar (RN-192).
  const [campos, setCampos] = useState({});
  const [origenes, setOrigenes] = useState({});
  const [errorDeGuardado, setErrorDeGuardado] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [acuse, setAcuse] = useState(null);
  const [propagacion, setPropagacion] = useState(null);
  const [errorDePropagacion, setErrorDePropagacion] = useState(null);
  const [confirmandoBaja, setConfirmandoBaja] = useState(null);

  // UNA SOLA LLAMADA (D-168, RN-235): §2 trae la sugerencia Y la identidad de la
  // línea guardada. El tablero (§1) no se consulta desde aquí.
  const consultar = useCallback(
    () => consultarSugerencias({ idPeriodo, fechaLocal: hoy }),
    [idPeriodo, hoy],
  );

  const listado = useConsultaDeListado(consultar, (respuesta) => (respuesta?.sugerencias ?? []).length === 0);

  const respuesta = listado.datos;
  const anio = anioDePeriodo(idPeriodo);
  const sugerencias = useMemo(() => respuesta?.sugerencias ?? [], [respuesta]);

  function teclear(idCategoria, texto) {
    setCampos((previos) => ({ ...previos, [idCategoria]: texto }));
    // RN-227: lo tecleado ES una captura manual, aunque se haya copiado de la
    // sugerencia y aunque la línea leída viniera propagada.
    setOrigenes((previos) => ({ ...previos, [idCategoria]: ORIGENES.capturaManual }));
  }

  function usarLaSugerida(sugerencia) {
    setCampos((previos) => ({ ...previos, [sugerencia.idCategoria]: sugerencia.importeSugerido }));
    setOrigenes((previos) => ({
      ...previos,
      [sugerencia.idCategoria]: sugerencia.claveBase === BASES.anioAnterior
        ? ORIGENES.sugerenciaAnioAnterior
        : ORIGENES.sugerenciaPromedio,
    }));
  }

  /**
   * Arma el lote. `SUGERENCIA_ANIO_ANTERIOR` viaja SIEMPRE con `importeSugerido`
   * —sin él, PARAMETRO_INVALIDO (RN-234)— y `CAPTURA_MANUAL` viaja SIN él
   * (RN-227). El porcentaje NO viaja por línea: lo estampa el servidor (D-131).
   */
  function armarLineas() {
    const lineas = [];

    for (const sugerencia of sugerencias) {
      const tecleado = campos[sugerencia.idCategoria];
      if (tecleado === undefined || String(tecleado).trim() === '') continue;

      const interpretado = interpretarImporte(tecleado, DIVISA_DEL_PRESUPUESTO);
      if (interpretado.estado !== ENTRADA.VALIDA) {
        return { error: `Revisa el importe de ${sugerencia.nombreCategoria}: no es un importe válido.` };
      }

      const claveOrigen = origenes[sugerencia.idCategoria] ?? ORIGENES.capturaManual;
      const linea = {
        idCategoria: sugerencia.idCategoria,
        importeAsignado: interpretado.valor,
        claveOrigen,
      };

      if (claveOrigen === ORIGENES.sugerenciaAnioAnterior) linea.importeSugerido = sugerencia.importeSugerido;

      // NULO = ALTA · PRESENTE = EDICIÓN (RN-235c). Los dos testigos se reenvían
      // TAL COMO LLEGARON en la misma sugerencia; no se componen cruzando listas.
      if (sugerencia.idPresupuestoDeCategoria !== null && sugerencia.idPresupuestoDeCategoria !== undefined) {
        linea.idPresupuestoDeCategoria = sugerencia.idPresupuestoDeCategoria;
        linea.version = sugerencia.version;
      }

      lineas.push(linea);
    }

    return { lineas };
  }

  async function guardar() {
    setErrorDeGuardado(null);
    setAcuse(null);

    const armado = armarLineas();
    if (armado.error) {
      setErrorDeGuardado({ mensaje: armado.error });
      return;
    }
    if (armado.lineas.length === 0) {
      setErrorDeGuardado({ mensaje: `No hay ninguna línea capturada para guardar en ${nombreDePeriodo(idPeriodo)}.` });
      return;
    }

    setGuardando(true);
    try {
      const resultado = await guardarLineasDelMes(idPeriodo, { lineas: armado.lineas, fechaLocal: hoy });
      setCampos({});
      setOrigenes({});
      setAcuse(`Se guardaron ${armado.lineas.length} línea${armado.lineas.length === 1 ? '' : 's'} de ${nombreDePeriodo(idPeriodo)}.`);
      listado.recargar();

      // CUÁNDO PREGUNTAR LO DECIDE EL SERVIDOR (RN-230b): con la lista vacía o sin
      // meses siguientes —diciembre— no se pregunta nada, y quitar una línea
      // tampoco pregunta, porque quitar no es cambiar de importe.
      const cambiadas = resultado.categoriasConImporteCambiado ?? [];
      if (cambiadas.length > 0 && (resultado.mesesSiguientesDelAnio ?? 0) > 0) {
        setPropagacion({ categorias: cambiadas, mesesSiguientesDelAnio: resultado.mesesSiguientesDelAnio });
      }
    } catch (fallo) {
      setErrorDeGuardado(fallo);
    } finally {
      setGuardando(false);
    }
  }

  async function propagar(categorias) {
    setErrorDePropagacion(null);
    try {
      // La vista previa es la que trae los testigos de CADA línea que se va a
      // modificar, y son los que hay que devolver en la escritura (RN-230j).
      const vistaPrevia = await propagarAMesesSiguientes(idPeriodo, {
        categorias, soloVistaPrevia: true, fechaLocal: hoy,
      });

      await propagarAMesesSiguientes(idPeriodo, {
        categorias,
        soloVistaPrevia: false,
        confirmado: true,
        fechaLocal: hoy,
        xminPorLinea: vistaPrevia?.xminPorLinea ?? [],
      });

      setPropagacion(null);
      setAcuse(`El importe se aplicó a los meses siguientes de ${anio}. ${anio + 1} no cambió.`);
      listado.recargar();
    } catch (fallo) {
      setErrorDePropagacion(fallo);
    }
  }

  async function quitarPresupuesto(sugerencia) {
    setErrorDeGuardado(null);
    // Sin línea guardada no hay nada que quitar: es el caso del alta (RN-235c).
    if (sugerencia.idPresupuestoDeCategoria === null || sugerencia.idPresupuestoDeCategoria === undefined) return;

    try {
      await eliminarLinea(sugerencia.idPresupuestoDeCategoria, {
        confirmado: true, version: sugerencia.version, fechaLocal: hoy,
      });
      setConfirmandoBaja(null);
      setAcuse(`${sugerencia.nombreCategoria} se quedó sin presupuesto en ${nombreDePeriodo(idPeriodo)}. Su gasto sigue visible en el tablero.`);
      listado.recargar();
    } catch (fallo) {
      setConfirmandoBaja(null);
      setErrorDeGuardado(fallo);
    }
  }

  return (
    <Shell titulo="Presupuesto" subtitulo={`Registro · ${etiquetaDePeriodo(idPeriodo)}`}>
      <ListadoActualizando visible={listado.actualizando} />

      {/* PORCENTAJE VIGENTE · SOLO LECTURA, dos aspectos y ninguno bloquea. Vacío
          cuando nadie lo ha fijado: no se pinta un "0 %" que nadie escribió
          (D-144, RN-215, RN-223). */}
      {respuesta && (
        <div className="card" style={{ margin: 12 }} data-prueba="porcentaje-vigente">
          <div className="fila">
            <span className="crece">Porcentaje vigente de {anio} · dato de lectura</span>
            <b data-prueba="valor-del-porcentaje">
              {respuesta.porcentajeAnualFijado ? porcentaje(respuesta.porcentajeAnualVigente) : 'Sin porcentaje'}
            </b>
          </div>
          <span className="ayuda">
            {respuesta.porcentajeAnualFijado
              ? 'Es el que se usó para calcular las sugerencias de abajo.'
              : `Nadie lo ha fijado para ${anio} y no hace falta: las sugerencias con base en el año anterior salen con el mismo importe del año pasado, tal cual.`}
            {' '}<b>Aquí no se captura</b>: el porcentaje se fija al definir el presupuesto del año.
          </span>
          <Link className="btn sec ancho" to={`/presupuesto/anio/${anio}?desde=${idPeriodo}`}>
            {respuesta.porcentajeAnualFijado
              ? `Cambiarlo en «Definir el presupuesto de ${anio}»`
              : `Definir el presupuesto de ${anio}`}
          </Link>
        </div>
      )}

      {listado.estado === ESTADO_DE_LISTADO.CARGANDO && <ListadoCargando texto="Consultando las sugerencias del mes…" />}
      {listado.estado === ESTADO_DE_LISTADO.ERROR && (
        <ListadoConError error={listado.error} alReintentar={listado.recargar} />
      )}

      {acuse && <div className="aviso s" role="status" data-prueba="acuse-de-guardado">{acuse}</div>}

      <AvisoDeError
        error={errorDeGuardado}
        datoDePrueba="error-de-guardado"
        acciones={<span className="ayuda">El guardado es en lote: o entran todas las líneas o no entra ninguna.</span>}
      />

      {propagacion && (
        <DialogoDePropagacion
          idPeriodo={idPeriodo}
          categoriasConImporteCambiado={propagacion.categorias}
          mesesSiguientesDelAnio={propagacion.mesesSiguientesDelAnio}
          error={errorDePropagacion}
          alSoloEsteMes={() => {
            // EL SILENCIO NUNCA PROPAGA: cerrar no invoca la operación (RN-230e).
            setPropagacion(null);
            setErrorDePropagacion(null);
            setAcuse(`El cambio quedó solo en ${nombreDePeriodo(idPeriodo)}. No se tocó ningún otro mes.`);
          }}
          alPropagar={propagar}
        />
      )}

      {confirmandoBaja && (
        <div className="aviso w" role="dialog" aria-modal="true" data-prueba="confirmar-quitar-presupuesto">
          <b>Quitar el presupuesto de {confirmandoBaja.nombreCategoria}</b>
          <span className="ayuda">
            Se quitará el presupuesto de <b>{confirmandoBaja.nombreCategoria}</b> para{' '}
            <b>{nombreDePeriodo(idPeriodo)}</b>. Lo gastado seguirá visible, sin comparación contra
            ninguna cifra. Es baja lógica y <b>no propaga nada</b>.
          </span>
          <div className="fila">
            <button className="btn" type="button" data-prueba="confirmar-baja"
              onClick={() => quitarPresupuesto(confirmandoBaja)}>
              Sí, quitar el presupuesto
            </button>
            <button className="btn sec" type="button" onClick={() => setConfirmandoBaja(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {respuesta && (
        <div className="card lista" style={{ margin: 12 }} data-prueba="lista-de-categorias">
          <div className="gtit">Categorías de gasto · {nombreDePeriodo(idPeriodo)}</div>
          <span className="ayuda">
            Orden <b>alfabético, resuelto por el servidor</b>; la pantalla no reordena ni agrupa.
            Un campo <b>vacío</b> significa «esta categoría se queda sin presupuesto»: nunca se
            sugiere 0.00. Todo es en <b>pesos</b>. Ambas personas editan todo.
          </span>

          {sugerencias.map((sugerencia) => (
            <RenglonDeCategoria
              key={sugerencia.idCategoria}
              sugerencia={sugerencia}
              idPeriodo={idPeriodo}
              enFoco={idCategoriaEnFoco === sugerencia.idCategoria}
              valor={campos[sugerencia.idCategoria]}
              alTeclear={(texto) => teclear(sugerencia.idCategoria, texto)}
              alUsarLaSugerida={() => usarLaSugerida(sugerencia)}
              alQuitar={() => setConfirmandoBaja(sugerencia)}
            />
          ))}
        </div>
      )}

      {respuesta && (
        <div style={{ margin: 12 }}>
          <button className="btn ancho" type="button" disabled={guardando} onClick={guardar}
            data-prueba="guardar-presupuesto">
            Guardar el presupuesto de {etiquetaDePeriodo(idPeriodo)}
          </button>
          <span className="ayuda">
            Un solo botón guarda todas las líneas del mes, en una sola operación. El porcentaje del
            año <b>no se guarda desde aquí</b>.
          </span>
          <Link className="btn sec ancho" to="/presupuesto">Ver el tablero</Link>
        </div>
      )}
    </Shell>
  );
}

/** Un renglón de categoría. Lo que explica viene ya redactado del servidor (RNF-053). */
function RenglonDeCategoria({
  sugerencia, idPeriodo, enFoco, valor, alTeclear, alUsarLaSugerida, alQuitar,
}) {
  const desactivada = !sugerencia.categoriaActiva;
  // LOS CUATRO CAMPOS SON UN BLOQUE (RN-235c): con importeActual vienen también el
  // identificador, el testigo y el origen; sin él no viene ninguno.
  const hayGuardado = sugerencia.importeActual !== null && sugerencia.importeActual !== undefined;
  const valorEnCampo = valor !== undefined ? valor : (sugerencia.importeActual ?? '');
  const idDelCampo = `presupuesto-${sugerencia.idCategoria}`;

  return (
    <div
      className="fila"
      data-prueba="renglon-de-categoria"
      data-categoria={sugerencia.idCategoria}
      style={desactivada ? { opacity: 0.72 } : undefined}
    >
      <div className="crece">
        <label htmlFor={idDelCampo}><b>{sugerencia.nombreCategoria}</b></label>

        {/* CATEGORÍA DESACTIVADA: atenuada, EDITABLE, sin sugerencia y sin alta
            (RN-206, RN-226b, RN-226c). */}
        {desactivada && (
          <small data-prueba="categoria-desactivada">
            <b>Categoría desactivada</b> · su línea se edita, pero no se puede dar de alta otra.
          </small>
        )}

        <div>
          {hayGuardado && (
            <span className="chip" data-prueba="cifra-guardada">Guardada {importe(sugerencia.importeActual)}</span>
          )}
          {sugerencia.importeSugerido ? (
            <span className="chip ok" data-prueba="cifra-sugerida">Sugerida {importe(sugerencia.importeSugerido)}</span>
          ) : (
            !desactivada && <span className="chip" data-prueba="sin-sugerencia">Sugerida — sin cifra —</span>
          )}
          {/* ETIQUETA DE ORIGEN VISIBLE: se distingue a simple vista un cálculo de
              una decisión de una persona (RN-204). */}
          {sugerencia.claveOrigenDeImporte && (
            <span className="chip" data-prueba="origen-guardado">
              Guardada como {etiquetaDeOrigen(sugerencia.claveOrigenDeImporte)}
            </span>
          )}
        </div>

        <small data-prueba="explicacion-de-la-sugerencia">{sugerencia.explicacion}</small>

        <div className="campo">
          <CampoDeImporte
            id={idDelCampo}
            aria-label={`Presupuesto de ${sugerencia.nombreCategoria}`}
            valor={valorEnCampo}
            alCambiar={alTeclear}
            codigoDivisa={DIVISA_DEL_PRESUPUESTO}
            contexto="el presupuesto"
            placeholder="Sin presupuesto"
            autoFocus={enFoco}
          />
        </div>

        <div>
          {sugerencia.importeSugerido && (
            <button className="btn sec" type="button" onClick={alUsarLaSugerida} data-prueba="usar-la-sugerida">
              Usar la sugerida
            </button>
          )}
          {hayGuardado && (
            <button className="btn sec" type="button" onClick={alQuitar} data-prueba="quitar-presupuesto">
              Quitar presupuesto
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function etiquetaDeOrigen(clave) {
  switch (clave) {
    case ORIGENES.capturaManual: return 'captura manual';
    case ORIGENES.sugerenciaAnioAnterior: return 'sugerencia año anterior';
    case ORIGENES.sugerenciaPromedio: return 'sugerencia por promedio';
    case 'PROPAGADO_DE_OTRO_MES': return 'propagada de otro mes';
    default: return clave;
  }
}
